import React, { Fragment, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashAlt, faEdit, faPlus, faSearch } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import api from '../../api/api';
import { Dialog, DialogBackdrop, Transition } from '@headlessui/react';
import LoadingLogo from '../../components/Loading';
import Pagination from '../../components/Pagination';



const PetAllergy = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editData, setEditData] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [meta, setMeta] = useState({
    total: 0,
    per_page: 10,
    current_page: 1,
    last_page: 1,
    from: 1,
    to: 1
  });

  // Fetch pet allergies on mount and when filters change
  useEffect(() => {
    fetchData();
  }, [filter, currentPage]);

  // Fetch pet allergies from the backend with search, filter, and pagination
  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/pet-allergies`, {
        params: {
          search: searchTerm,
          filter,
          page: currentPage,
        },
      });
      setData(response.data); // Set the pet allergies
      setMeta(response.meta); // Update pagination metadata
      setLoading(false);
    } catch (error) {
      toast.error('Failed to fetch pet allergies.');
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this pet allergy?')) {
      try {
        await api.delete(`/pet-allergies/${id}`);
        toast.success('Pet allergy deleted successfully.');
        fetchData(); // Refresh the table after deletion
      } catch (error) {
        toast.error('Failed to delete pet allergy.');
      }
    }
  };

  const handleEdit = (petType) => {
    setEditData(petType);
    setIsModalOpen(true); // Show modal for editing
  };

  const handleAdd = () => {
    setEditData(null); // Reset any edit data
    setIsModalOpen(true); // Show modal for adding
  };

  const handleSave = () => {
    setIsModalOpen(false); // Hide modal after saving
    fetchData(); // Refresh list
  };

  const handleCancel = () => {
    setIsModalOpen(false); // Hide modal without saving
  };

  // Pagination handlers
  const handleNextPage = () => {
    if (meta.current_page < meta.last_page) {
      setCurrentPage(meta.current_page + 1);
    }
  };

  const handlePrevPage = () => {
    if (meta.current_page > 1) {
      setCurrentPage(meta.current_page - 1);
    }
  };

  // Render individual page buttons
  const renderPageNumbers = () => {
    const pages = [];
    for (let i = 1; i <= meta.last_page; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => setCurrentPage(i)}
          className={`px-3 py-1 rounded-lg border border-tblue ${i === meta.current_page ? 'bg-tblue text-white' : 'bg-white text-gray-700'
            } hover:bg-tblue hover:text-white mx-1`}
        >
          {i}
        </button>
      );
    }
    return pages;
  };


  if (loading) {
    return <LoadingLogo />;
  }

  return (
    <div className="mx-auto px-4  max-w-full overflow-x-hidden">
      {/* Header and Actions - Stack on mobile */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 space-y-4 md:space-y-0">
        <h1 className="text-tblue text-3xl font-bold">Pet Allergies</h1>
        <button
          onClick={handleAdd}
          className="bg-primary text-white px-4 py-2 rounded-lg flex items-center hover:bg-accent w-full md:w-auto justify-center"
        >
          <FontAwesomeIcon icon={faPlus} className="mr-2" />
          Add New Pet Allergy
        </button>
      </div>

      {/* Search and Filter - Stack on mobile */}
      <div className="flex flex-col sm:flex-row sm:space-x-4 space-y-4 sm:space-y-0 mb-6">
        {/* Search Input */}
        <div className="flex-1 flex items-center border border-gray-300 rounded-lg p-2 shadow-md">
          <FontAwesomeIcon
            icon={faSearch}
            className="text-gray-400 mr-2 transition-transform duration-300 hover:scale-110"
          />
          <input
            type="text"
            placeholder="Search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                fetchData();
              }
            }}
            className="outline-none focus:ring-0 bg-transparent w-full text-gray-700 placeholder-gray-400 focus:placeholder-gray-500 focus:text-black"
          />
        </div>

        {/* Filter Dropdown */}
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="border rounded-lg p-2 shadow-sm w-full sm:w-auto"
        >
          <option value="all">All Pet Allergies</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      {/* Table section - Make it scrollable horizontally on mobile */}
      <div className="overflow-x-auto relative rounded-lg">
        <table className="min-w-full bg-white rounded-lg shadow-md">
          <thead className="bg-primary text-white">
            <tr>
              <th className="py-4 px-2 text-left sticky left-0 bg-primary z-2">ID</th>
              <th className="py-4 px-2 text-left ">Name</th>
              <th className="py-4 px-2 text-left ">Is Active</th>
              <th className="py-4 px-2 text-center">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {data.length > 0 ? (
              data.map((pet) => (
                <tr key={pet.id} className="hover:bg-gray-50">
                  <td className="py-2 px-2 font-semibold text-gray-700 whitespace-nowrap sticky left-0 bg-white z-2 border-r border-gray-200">
                    {pet.id}
                  </td>

                  <td className="py-2 px-2 sm:table-cell">
                    {pet.name}
                  </td>
                  <td className="py-2 px-2  sm:table-cell">
                    <span className={`px-2 py-1 rounded-lg text-sm ${pet.is_active
                        ? "bg-green-100 text-green-600"
                        : "bg-red-100 text-red-600"
                      }`}>
                      {pet.is_active ? "Yes" : "No"}
                    </span>
                  </td>
                  <td className="py-2 px-2 text-center">
                    <div className="flex justify-center space-x-2">
                      <button
                        onClick={() => handleEdit(pet)}
                        className="text-blue-500 hover:text-blue-700 p-2"
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                      <button
                        onClick={() => handleDelete(pet.id)}
                        className="text-red-500 hover:text-red-700 p-2"
                      >
                        <FontAwesomeIcon icon={faTrashAlt} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="3" className="py-4 text-center text-gray-500">
                  No pet allergies available.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Smart Pagination */}
      <Pagination
        meta={meta}
        onPageChange={(page) => setCurrentPage(page)}
      />

      {/* CRUD Modal for Adding/Editing */}
      {isModalOpen && (
        <CRUModal
          item={editData}
          onSave={handleSave}
          onCancel={handleCancel}
        />
      )}
    </div>
  );
};

export default PetAllergy;

export const CRUModal = ({ item, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    is_active: true,
  });

  useEffect(() => {
    if (item) {
      setFormData(item); // Populate form data if editing
    }
  }, [item]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (item) {
        // Update Pet Allergy
        await api.put(`/pet-allergies/${item.id}`, formData);
        toast.success('Pet allergy updated successfully');
      } else {
        // Create Pet Allergy
        await api.post('/pet-allergies', formData);
        toast.success('Pet allergy created successfully');
      }
      onSave(); // Callback to refresh the list and close the form
    } catch (error) {
      toast.error('Error saving pet allergy. Please check the form.');
    }
  };

  return (
    <Transition appear show={true} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onCancel}>
        <DialogBackdrop className="fixed inset-0 bg-black/30 backdrop-blur-sm" />
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title
                  as="h3"
                  className="text-lg font-medium leading-6 text-gray-900"
                >
                  {item ? 'Edit Pet Allergy' : 'Add New Pet Allergy'}
                </Dialog.Title>
                <div className="mt-4">
                  <form onSubmit={handleSubmit}>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="block w-full p-2 border rounded-lg"
                      />
                    </div>

                    <div className="flex items-center mb-4">
                      <label className="block text-sm font-medium text-gray-700 mr-4">Active</label>
                      <input
                        type="checkbox"
                        name="is_active"
                        checked={formData.is_active}
                        onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                        className="h-4 w-4"
                      />
                    </div>

                    <div className="flex justify-end mt-6">
                      <button
                        type="button"
                        onClick={onCancel}
                        className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg mr-2"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="bg-primary text-white px-4 py-2 rounded-lg"
                      >
                        {item ? 'Update' : 'Save'}
                      </button>
                    </div>
                  </form>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
